import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import useNavigate from '@hook/useNavigate'
import { useWireState } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import config from '@/config'
import { useCallbackRef } from '@hook/useCallbackRef'
import { useLabelStudio } from '@hook/useLabelStudio'
import {
    RECORD_TYPE_CHECK,
    GCS_BUCKET_CHECKS,
    CHECK_STATUS_PROCESSED,
} from '@constants'
import { loadImageWithAuthorization, createSyntheticDownload } from '@utils'
import { toast } from 'react-toastify'

export const validCheckStatuses = [
    CHECK_STATUS_PROCESSED,
]

const CheckDetailsViewModel = (isAnnotationsPage = false) => {
    
    const { checkId } = useParams()
    const navigate = useNavigate()
    
    const [refEditor, editorNode, clearElement] = useCallbackRef()
    
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const [pageIndex, setPageIndex] = useState(0)
    const [checkDetails, setCheckDetails] = useState([])
    const [pageImageUrl, setPageImageUrl] = useState()
    const [isExporting, setIsExporting] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [showAllLabelsChecked, setShowAllLabelsChecked] = useState(false)
    
    const [checks, setChecks] = useWireState(store.checks)
    
    const check = useMemo(() => checks?.rows?.find(it => it?.id === checkId), [checks, checkId])
    const pageCount = useMemo(() => check?.pages?.length || 0, [check])
    
    // Not populated automatically
    const [checkWells, setCheckWells] = useState({})
    const [checkRevenue, setCheckRevenue] = useState({})
    const [checkProduction, setCheckProduction] = useState([])
    const [imageUrl, setImageUrl] = useState(null)
    
    const checkPageUrls = useMemo(() => check?.pages?.map(it => (
        `${config.parseApiBaseUrl}/api/v1/checks/files/${it.fileName}`
    )), [check])
    
    // @todo legacy?
    /* const canEditCheck = useMemo(() => {
        return validCheckStatuses.includes(check?.status)
    }, [check]) */
    
    const setCheck = updatedCheck => setChecks({
        ...checks,
        rows: (checks?.rows ?? []).map(it => (it.id === updatedCheck?.id ? updatedCheck : it)),
    })
    
    const { labelStudioError, hasOrganizationLabels } = useLabelStudio({
        enabled: isEditMode && check?.id && imageUrl?.length,
        url: imageUrl,
        gcsBucket: GCS_BUCKET_CHECKS,
        editorNode,
        record: check,
        recordType: RECORD_TYPE_CHECK,
        pageIndex,
        setRecord: setCheck,
        showAllLabels: showAllLabelsChecked,
        useOrganizationLabels: true,
        useCollectionLabels: false,
    })
    
    const toggleEditMode = useCallback(() => {
        setIsEditMode(!isEditMode)
    }, [isEditMode])
    
    const fetchCheckWells = async (checkId, offset, limit) => {
        
        try {
            
            const res = await actions.fetchCheckWells(checkId, offset, limit)
            
            setCheckWells(res)
            
        } catch (e) {
            
            console.error('CheckDetailsViewModel#fetchCheckWells', e)
            toast.error('Failed to fetch check wells')
            
        }
        
    }
    
    const fetchCheckRevenue = async checkId => {
        try {
            
            const res = await actions.fetchCheckRevenue(checkId)
            
            setCheckRevenue(res)
            
        } catch (e) {
            console.error('CheckDetailsViewModel#fetchCheckRevenue', e)
            toast.error('Failed to fetch check Revenue')
        }
    }
    
    const fetchCheckProduction = async checkId => {
        try {
            
            const res = await actions.fetchCheckProduction(checkId)
            
            setCheckProduction(res)
            
        } catch (e) {
            console.error('CheckDetailsViewModel#fetchCheckProduction', e)
            toast.error('Failed to fetch check Production')
        }
    }
    
    const onUpdateCheck = useCallback(async data => {
        
        try {
            
            const res = await actions.updateCheck(check.id, { values: data })
            
            console.log('onUpdateCheck', res)
            
        } catch (e) {
            
            console.error('onUpdateCheck', e)
            
            toast.error('Failed to update check')
            
        }
        
    }, [check])
    
    const onExportCheckClick = useCallback(async () => {
        
        if (!check) return
        
        setIsExporting(true)
        
        try {
            
            const res = await actions.exportCheck(check.id)
            
            createSyntheticDownload(res.data, 'text/csv', `${check.name}.csv`)
            toast.success('Exported check')
            
        } catch (e) {
            
            console.error('onExportCheckClick', e)
            toast.error('Failed to export check')
            
        }
        
        setIsExporting(false)
        
    }, [check])
    
    const onDownloadCheckClick = useCallback(async () => {
        
        if (!check) return
        
        setIsDownloading(true)
        
        try {
            
            const res = await actions.streamFileUrl(check.fileName)
            
            createSyntheticDownload(res, 'application/pdf', check.fileName)
            toast.success('Downloaded check')
            
        } catch (e) {
            
            console.error('onDownloadCheckClick', e)
            toast.error('Failed to download check')
            
        }
        
        setIsDownloading(false)
        
    }, [check])
    
    const onUploadCheckClick = async files => {
        
        if (!check) return
        
        const validFiles = files.filter(it => it.name?.length)
        
        if (validFiles.length < 1) return
        
        const file = validFiles[0]
        
        try {
            
            setIsUploading(true)
            
            await actions.uploadFileToExistingCheck(check.id, file)
            
            toast.success('Check file uploaded successfully')
            
            setIsUploading(false)
            
        } catch (e) {
            toast.error('Failed to upload check')
        }
    }
    
    useEffect(() => {
        
        if (checkId)
            console.log('fetching check', checkId)
        
        if (checkId)
            actions.fetchCheckById(checkId, {
                withAnnotations: true,
                withAnnotationLabels: true,
            }).then(it => {
                if (!it) navigate.toChecks()
                else console.log('fetched check w/labels', it)
            })
        
    }, [checkId])
    
    useEffect(() => {
        
        const page = check?.pages?.[pageIndex]
        
        if (!page || isAnnotationsPage) return
        
        setLoading(true)
        
        Promise.allSettled([
            actions.fetchCheckDetailsByPageId(page.id)
                .then(it => setCheckDetails(it))
                .catch(e => {
                    console.error('CheckDetailsViewModel.effect(fetchCheckDetailsByPageId)', e)
                    setError(e)
                }),
            actions.streamFileUrl(page.fileName)
                .then(it => setPageImageUrl(URL.createObjectURL(it)))
                .catch(e => {
                    console.error(`CheckDetailsViewModel.effect(streamFileUrl(${page.fileName}))`, e)
                    setError(e)
                }),
        ]).then(() => setLoading(false))
        
    }, [check, isAnnotationsPage, pageIndex])
    
    useEffect(() => {
        setShowAllLabelsChecked(!hasOrganizationLabels)
    }, [hasOrganizationLabels])
    
    useEffect(() => {
        
        if (!checkPageUrls?.[pageIndex ?? 0]?.length)
            return
        
        loadImageWithAuthorization(checkPageUrls?.[pageIndex ?? 0])
            .then(it => setImageUrl(it.dataUrl))
            .catch(e => {
                console.error('Failed to load page image for LS', e)
                
                // @todo show error
            })
        
    }, [checkPageUrls, pageIndex])
    
    useEffect(() => {
        
        // When we exit edit mode, we need to clear the useCallbackRef
        // element, since removing/adding it to the DOM will change it's ref
        if (!isEditMode)
            clearElement()
        
    }, [clearElement, isEditMode])
    
    return {
        
        refEditor,
        checkId,
        navigate,
        
        error,
        loading,
        pageCount,
        pageIndex,
        setPageIndex,
        checkDetails,
        pageImageUrl,
        isExporting,
        isDownloading,
        isUploading,
        isEditMode,
        setIsEditMode,
        showAllLabelsChecked,
        setShowAllLabelsChecked,
        
        checks,
        check,
        checkWells,
        checkRevenue,
        checkProduction,
        
        labelStudioError,
        
        toggleEditMode,
        fetchCheckWells,
        fetchCheckRevenue,
        fetchCheckProduction,
        onUpdateCheck,
        onExportCheckClick,
        onDownloadCheckClick,
        onUploadCheckClick,
        
    }
    
}

export default CheckDetailsViewModel
